import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import {
  passwordChangePageQueryOptions,
  type PasswordChangePageQueryInput,
} from "~/app/api/pageData/passwordChangePageQuery";
import type { PasswordChangePageInitialData } from "~/app/api/pageData/passwordChangePageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createChangePasswordPage } from "~/pages/ChangePasswordPage/ChangePasswordPage";
import { createExpiredPasswordChangePage } from "~/pages/ExpiredPasswordChangePage/ExpiredPasswordChangePage";

/**
 * Normalizes the token/new-customer route inputs so history navigation and equivalent URLs share
 * stable loader dependencies and page-data query keys.
 */
export function parsePasswordChangeSearch(search: Record<string, unknown>): PasswordChangePageQueryInput {
  const token = typeof search.token === "string" && search.token ? search.token : null;
  return {
    token,
    newCustomer: search.newCustomer === true || search.newCustomer === "true",
  };
}

function PasswordChangeRoute() {
  const result = useLoaderData({
    from: "/login/pw/change",
  }) as PageInitialData<PasswordChangePageInitialData | void>;
  const shellProps = useStorefrontShellProps();
  const data = result.data;

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {data
        ? createChangePasswordPage({
            token: data.token,
            isNewUser: Boolean(data.newCustomer),
            expirationDays: data.expirationDays,
          })
        : createExpiredPasswordChangePage()}
    </StorefrontShellFrame>
  );
}

export async function loadPasswordChangePageData(
  context: { queryClient: QueryClient },
  search: PasswordChangePageQueryInput,
) {
  const result = await context.queryClient.ensureQueryData(passwordChangePageQueryOptions(search));

  return unwrapPageInitialData(result);
}

export const passwordChangeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login/pw/change",
  validateSearch: parsePasswordChangeSearch,
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => loadPasswordChangePageData(context, deps),
  component: PasswordChangeRoute,
});
